import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../services/api';

interface LoginForm {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LoginForm>({
    email: '',
    password: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.login(formData);
      
      // Store token and user data
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      // Notify other components about auth state change
      window.dispatchEvent(new CustomEvent('authStateChanged'));
      
      // Redirect based on user role
      const roles = response.user.roles?.map((r: any) => r.name) || [];
      if (roles.includes('pos_cashier') && !roles.includes('super_admin') && !roles.includes('pos_admin')) {
        // Cashier goes straight to POS terminal
        navigate('/pos');
      } else if (response.user.roles?.some((role: any) => role.is_admin)) {
        navigate('/admin/dashboard');
      } else {
        navigate('/');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-[41px]">
      {/* Simple Breadcrumb */}
      <div className="border-b border-gray-200" style={{ backgroundColor: '#f7f1f0' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-8 text-center">
            {/* Page Title */}
            <h1 className="text-3xl font-semibold text-gray-600 tracking-wide mb-4">MY ACCOUNT</h1>
            {/* Breadcrumb Navigation */}
            <nav className="flex items-center justify-center space-x-2 text-sm" aria-label="Breadcrumb">
              <Link 
                to="/" 
                className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                Home
              </Link>
              <span className="text-gray-400">/</span>
              <span className="text-gray-600">My account</span>
            </nav>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="flex flex-col justify-center py-12">
        <div className="max-w-lg mx-auto w-full">
          <div className="bg-white border border-gray-200 p-8">
            <h2 className="text-2xl font-light text-center text-gray-700 mb-8">Login</h2>
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm text-gray-600 mb-2">
                Username or Email address *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-3 border-b border-gray-300 focus:border-gray-500 focus:outline-none bg-transparent"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm text-gray-600 mb-2">
                Password *
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border-b border-gray-300 focus:border-gray-500 focus:outline-none bg-transparent pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                      <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center">
                <input type="checkbox" className="mr-2" />
                <span className="text-gray-600">Remember me</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-gray-600 hover:text-gray-800"
              >
                lost your password?
              </Link>
            </div>

            <div className="mt-6">
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-gray-800 text-white font-medium text-sm tracking-wide hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    LOGGING IN...
                  </div>
                ) : (
                  'LOG IN'
                )}
              </button>
            </div>

            <div className="text-center mt-6 text-sm text-gray-600">
              No account yet?{' '}
              <Link
                to="/register"
                className="text-gray-800 underline hover:text-gray-600"
              >
                Create an account
              </Link>
            </div>
          </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
